import { Component } from '@theme/component';
import { getIntersectionRoot, scrollContainerMediaQuery } from '@theme/scroll-container';

/** @type {Set<TmDeferredMedia>} */
const pending = new Set();

/** @type {IntersectionObserver | null} */
let observer = null;

/**
 * Builds the shared observer on the current page scroll container: `.page-wrapper` from
 * 990 px (Horizon squeeze layout), the viewport below. Rebuilt when that breakpoint flips.
 */
function connectObserver() {
  observer?.disconnect();
  if (!('IntersectionObserver' in window)) {
    observer = null;
    for (const element of pending) element.load();
    return;
  }

  observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && entry.target instanceof TmDeferredMedia) entry.target.load();
      }
    },
    { root: getIntersectionRoot(), rootMargin: '600px 0px' }
  );
  for (const element of pending) observer.observe(element);
}

scrollContainerMediaQuery.addEventListener('change', connectObserver);

/**
 * Below-the-fold photo of the v8 homepage (tm-deferred-media snippet). The image lives in a
 * `<template>` so it is not fetched during the LCP window; a `<noscript>` copy keeps it for
 * visitors without JavaScript. The container reserves the space, so insertion causes no shift.
 */
class TmDeferredMedia extends Component {
  connectedCallback() {
    super.connectedCallback();
    if (!this.querySelector(':scope > template')) return;

    // data-after-load: media close to a heavy LCP image waits for the load event first.
    if (this.hasAttribute('data-after-load') && document.readyState !== 'complete') {
      window.addEventListener('load', () => this.isConnected && this.#watch(), { once: true });
      return;
    }
    this.#watch();
  }

  #watch() {
    pending.add(this);
    if (!observer) connectObserver();
    observer?.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    pending.delete(this);
    observer?.unobserve(this);
  }

  /**
   * Replaces the template with its image, once. Media sharing a horizontal track load
   * together: the track is a nested scroller, so off-track items never reach the margin.
   */
  load() {
    const track = this.closest('.tm-rail__track, .tm-tiles__track');
    const group = track ? track.querySelectorAll('tm-deferred-media') : [this];

    for (const element of group) {
      if (!(element instanceof TmDeferredMedia)) continue;
      pending.delete(element);
      observer?.unobserve(element);

      const template = element.querySelector(':scope > template');
      if (template instanceof HTMLTemplateElement) template.replaceWith(template.content.cloneNode(true));
    }
  }
}

if (!customElements.get('tm-deferred-media')) {
  customElements.define('tm-deferred-media', TmDeferredMedia);
}
